/**
 * Phase 24.4b Step 6 — composes 7 DayColumns + responsive layout.
 *
 * <1024px → mobile accordion, today (or week-start) defaults expanded.
 * >=1024px → desktop expanded grid (all 7 columns visible).
 *
 * Single source of truth for responsive state via useMediaQuery
 * (per-spec — no parallel CSS-driven expansion that would conflict
 * with Zustand expandedDays).
 */
import { useMemo } from "react";
import type { MealPlanRead } from "@/api/meal_plan";
import { useMealPlanUi } from "@/stores/meal_plan_ui";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { DayColumn } from "./DayColumn";

interface Props {
  plan: MealPlanRead;
  onMealClick: (itemId: number, recipeId: number) => void;
}

function defaultExpandedDay(plan: MealPlanRead): string {
  const todayIso = new Date().toISOString().slice(0, 10);
  const inWeek = plan.days.some((d) => d.date === todayIso);
  return inWeek ? todayIso : plan.week_start_date;
}

export function WeekGrid({ plan, onMealClick }: Props) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { expandedDays, toggleDay } = useMealPlanUi();

  const fallbackExpanded = useMemo(() => defaultExpandedDay(plan), [plan]);

  function isExpanded(date: string): boolean {
    if (isDesktop) return true;
    if (expandedDays.length === 0) return date === fallbackExpanded;
    return expandedDays.includes(date);
  }

  return (
    <div
      className="grid gap-3 lg:grid-cols-7 lg:gap-2"
      data-testid="week-grid"
    >
      {plan.days.map((day) => (
        <DayColumn
          key={day.date}
          day={day}
          expanded={isExpanded(day.date)}
          onToggle={isDesktop ? undefined : () => toggleDay(day.date)}
          onMealClick={onMealClick}
        />
      ))}
    </div>
  );
}
